import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * 개인정보처리방침 페이지 (/privacy)
 *
 * 구글 애드센스 승인 요건 충족용:
 *  - 제3자 광고(구글 애드센스) 게재 고지
 *  - 쿠키 사용 및 맞춤형 광고 안내
 *  - 사용자가 광고 개인 최적화를 거부할 수 있는 방법 안내
 *
 * ※ 아래 운영자/연락처 정보(SITE_CONTACT)는 실제 정보로 교체하세요.
 */

const SITE_NAME = 'CBT 편의점';
const SITE_DOMAIN = 'www.cbtstudy.com';
const SITE_CONTACT = 'support@cbtstudy.com'; // TODO: 실제 운영 이메일로 교체
const EFFECTIVE_DATE = '2026년 6월 3일';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>개인정보처리방침 - {SITE_NAME}</title>
        <meta
          name="description"
          content={`${SITE_NAME}의 개인정보처리방침입니다. 수집하는 개인정보 항목, 이용 목적, 쿠키 및 제3자 광고(구글 애드센스) 정책을 안내합니다.`}
        />
        <link rel="canonical" href={`https://${SITE_DOMAIN}/privacy`} />
      </Helmet>

      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="text-lg font-bold text-gray-900">
            {SITE_NAME}
          </a>
          <a
            href="/"
            className="text-sm text-orange-500 hover:text-orange-600 transition-colors"
          >
            ← 홈으로
          </a>
        </div>
      </header>

      {/* 본문 */}
      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">개인정보처리방침</h1>
        <p className="text-sm text-gray-500 mb-10">시행일: {EFFECTIVE_DATE}</p>

        <p className="text-gray-700 leading-relaxed mb-8">
          {SITE_NAME}(이하 "서비스")는 이용자의 개인정보를 중요하게 생각하며,
          「개인정보 보호법」 등 관련 법령을 준수합니다. 본 개인정보처리방침은
          서비스가 어떤 정보를 수집하고 어떻게 이용·보관하는지를 설명합니다.
        </p>

        <Section title="1. 수집하는 개인정보 항목">
          <p>서비스는 다음과 같은 정보를 수집할 수 있습니다.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>회원가입·로그인 시(선택):</strong> 소셜 로그인 제공자(Google
              등)로부터 전달받는 이메일 주소, 프로필 이름
            </li>
            <li>
              <strong>학습 이용 정보:</strong> 문제 풀이 기록, 정답·오답 내역, 학습
              진도 및 통계(브라우저 로컬 저장소 또는 로그인 시 클라우드에 저장)
            </li>
            <li>
              <strong>자동 수집 정보:</strong> 쿠키, 접속 기록, 기기·브라우저 정보,
              IP 주소 등 서비스 이용 과정에서 자동으로 생성·수집되는 정보
            </li>
          </ul>
          <p>
            로그인 없이도 학습 기능을 이용할 수 있으며, 이 경우 학습 기록은
            이용자의 브라우저에만 저장됩니다.
          </p>
        </Section>

        <Section title="2. 개인정보의 이용 목적">
          <ul className="list-disc pl-5 space-y-1">
            <li>회원 식별 및 로그인, 학습 진도·오답노트의 저장과 동기화</li>
            <li>서비스 제공·운영 및 품질 개선</li>
            <li>맞춤형 학습 기능 제공 및 통계 분석</li>
            <li>광고 게재 및 서비스 부정 이용 방지</li>
          </ul>
        </Section>

        <Section title="3. 쿠키(Cookie)의 사용">
          <p>
            서비스는 이용자에게 보다 나은 경험을 제공하기 위해 쿠키를 사용합니다.
            쿠키는 이용자의 브라우저에 저장되는 작은 텍스트 파일로, 로그인 상태
            유지, 이용 환경 기억, 광고 게재 등에 사용됩니다.
          </p>
          <p>
            이용자는 웹 브라우저 설정을 통해 쿠키 저장을 거부하거나 삭제할 수
            있습니다. 다만 쿠키 저장을 거부하는 경우 로그인 유지 등 일부 기능
            이용에 제한이 있을 수 있습니다.
          </p>
        </Section>

        <Section title="4. 제3자 광고 및 구글 애드센스">
          <p>
            서비스는 제3자 광고 사업자인 <strong>Google AdSense</strong>를 통해
            광고를 게재합니다. Google을 포함한 제3자 광고 사업자는 쿠키를 사용하여
            이용자의 이전 방문 기록 등을 바탕으로 맞춤형 광고를 제공합니다.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Google은 광고 쿠키를 사용하여 이용자가 본 서비스 및 다른 웹사이트를
              방문한 기록을 기반으로 광고를 게재할 수 있습니다.
            </li>
            <li>
              이용자는{' '}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline"
              >
                Google 광고 설정
              </a>
              에서 개인 맞춤 광고를 거부(opt-out)할 수 있습니다.
            </li>
            <li>
              제3자 광고 사업자의 쿠키 사용에 대한 거부는{' '}
              <a
                href="https://www.aboutads.info/choices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline"
              >
                www.aboutads.info/choices
              </a>
              에서 설정할 수 있습니다.
            </li>
            <li>
              Google의 개인정보 처리에 대한 자세한 내용은{' '}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline"
              >
                Google 개인정보처리방침
              </a>
              을 참고하시기 바랍니다.
            </li>
          </ul>
        </Section>

        <Section title="5. 개인정보의 보유 및 이용 기간">
          <p>
            서비스는 원칙적으로 개인정보 수집·이용 목적이 달성되거나 이용자가 회원
            탈퇴를 요청한 경우 해당 정보를 지체 없이 파기합니다. 다만 관련 법령에
            따라 보존이 필요한 경우 해당 기간 동안 보관합니다.
          </p>
        </Section>

        <Section title="6. 개인정보의 제3자 제공">
          <p>
            서비스는 이용자의 개인정보를 본 방침에서 고지한 범위를 넘어 외부에
            제공하지 않습니다. 다만 법령에 근거하거나 수사기관의 적법한 요청이 있는
            경우는 예외로 합니다.
          </p>
        </Section>

        <Section title="7. 이용자의 권리와 행사 방법">
          <p>
            이용자는 언제든지 자신의 개인정보를 조회·수정하거나 수집·이용에 대한
            동의를 철회(회원 탈퇴)할 수 있습니다. 또한 브라우저에 저장된 학습 기록
            및 쿠키를 직접 삭제할 수 있습니다.
          </p>
        </Section>

        <Section title="8. 개인정보 보호책임자 및 문의">
          <p>
            개인정보 처리에 관한 문의·불만·피해구제 등은 아래 연락처로 문의하실 수
            있습니다.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>서비스명: {SITE_NAME}</li>
            <li>
              이메일:{' '}
              <a href={`mailto:${SITE_CONTACT}`} className="text-orange-600 underline">
                {SITE_CONTACT}
              </a>
            </li>
          </ul>
        </Section>

        <Section title="9. 개인정보처리방침의 변경">
          <p>
            본 개인정보처리방침은 법령 및 서비스 정책 변경에 따라 개정될 수 있으며,
            변경 시 본 페이지를 통해 공지합니다.
          </p>
        </Section>

        <div className="border-t border-gray-200 mt-10 pt-6 text-sm text-gray-500">
          <p>본 방침은 {EFFECTIVE_DATE}부터 시행됩니다.</p>
        </div>
      </main>
    </div>
  );
}
